import React, { useEffect, useState } from 'react'
import { getIdentityToken } from './authUtil'
import { jwtDecode } from 'jwt-decode'
import { ldapService } from '../services/ldapService'

interface AuthenticationControllerState {
    isLoggedIn: boolean
    skipLogin: boolean
    authenticatedUserAlias: string
}

export interface OutageToolPortalUserInfo {
    userAlias: string
    email: string
}

const DEFAULT_USER_INFO: OutageToolPortalUserInfo = {
    userAlias: 'Guest',
    email: 'guest@amazon.com'
}

const AuthenticatedUserContext = React.createContext<OutageToolPortalUserInfo>(DEFAULT_USER_INFO)

const AuthenticationController = (props: React.PropsWithChildren<any>) => {
    const [isLoggedIn, setLoggedIn] = useState(false)
    const [skipLogin, setSkipLogin] = useState(window.location.pathname.startsWith('/error'))
    const [authenticatedUserAlias, setAuthenticatedUserAlias] = useState(
        DEFAULT_USER_INFO.userAlias,
    )
    const [authenticatedUserEmail, setAuthenticatedUserEmail] = useState(
        DEFAULT_USER_INFO.email,
    )

    const [shouldRenderApp, setShouldRender] = useState(false)

    useEffect(() => {
        const REFRESH_CRED_INTERVAL = 30000;
        const isMidwayEnabled = process.env.REACT_APP_ENABLE_MIDWAY_AUTH === 'true';

        console.log('🔧 AUTH CONFIG: Midway authentication enabled:', isMidwayEnabled);

        const verifyAuthentication = () => {
            // Check if Midway authentication is disabled for local development
            if (!isMidwayEnabled) {
               
                const mockUserAlias = 'localdev';
                const mockEmail = 'localdev@amazon.com';
                
                setLoggedIn(true);
                setAuthenticatedUserAlias(mockUserAlias);
                setAuthenticatedUserEmail(mockEmail);
                
 
                return Promise.resolve();
            }

            // Use real Midway authentication
            return getIdentityToken()
                .then((data) => {
                    if (data) {
                        // Store token in localStorage for API calls
                        localStorage.setItem('midway_auth_token', data);
                        console.log('✅ [AUTH] Midway token stored in localStorage');
                        
                        const decodeJwt = jwtDecode<any>(data)
                  
                        
                        if (decodeJwt && decodeJwt.sub) {
                            setLoggedIn(true);
                            setAuthenticatedUserAlias(decodeJwt.sub);
                            
                            // Fetch email using LDAP service
                            ldapService.getUserEmail(decodeJwt.sub)
                                .then((result) => {
                                    if (result.success && result.email) {
                                        
                                        setAuthenticatedUserEmail(result.email);
                                    } else {
                                       
                                        setAuthenticatedUserEmail(`${decodeJwt.sub}@amazon.com`);
                                    }
                                })
                                .catch((error) => {
                                    console.error('❌ EMAIL LOOKUP ERROR:', error);
                                    setAuthenticatedUserEmail(`${decodeJwt.sub}@amazon.com`);
                                });
                        }
                    }
                })
                .catch((err) => {
                    console.error('❌ MIDWAY AUTH ERROR:', err);
                    setLoggedIn(false)
                })
        }

        verifyAuthentication()
        
        // Only set up refresh interval for real Midway auth
        let interval: NodeJS.Timeout | null = null;
        if (isMidwayEnabled) {
            interval = setInterval(verifyAuthentication, REFRESH_CRED_INTERVAL);
        }
        
        setShouldRender(isLoggedIn || skipLogin)

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        }
    }, [isLoggedIn, skipLogin])

    // Loading spinner component
    const LoadingSpinner = () => (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '18px'
        }}>
            <div style={{
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 2s linear infinite',
                marginRight: '10px'
            }}></div>
            Authenticating...
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    )

    return (
        <>
            {shouldRenderApp && (
                <AuthenticatedUserContext.Provider
                    value={{
                        userAlias: authenticatedUserAlias,
                        email: authenticatedUserEmail,
                    }}
                >
                    {props.children}
                </AuthenticatedUserContext.Provider>
            )}
            {!shouldRenderApp && <LoadingSpinner />}
        </>
    )
}

export { AuthenticationController, AuthenticatedUserContext }
